import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import { FlowApi, Flow, NewFlow, UpdateFlow, FlowFilters } from '../models';

const flowsService = createApi({
  reducerPath: 'flowsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/flows`),
  tagTypes: ['Flows'],
  endpoints: (builder) => ({
    getFlows: builder.query<Flow[], { filters?: FlowFilters }>({
      query: ({ filters }) => {
        const params: Record<string, string> = {};
        if (
          filters?.logisticOperatorId &&
          filters.logisticOperatorId.trim() !== ''
        ) {
          params.logisticOperatorId = filters.logisticOperatorId.trim();
        }
        if (filters?.name && filters.name.trim() !== '') {
          params.name = filters.name.trim();
        }
        return {
          url: '',
          params: Object.keys(params).length > 0 ? params : undefined,
        };
      },
      transformResponse: (response: FlowApi[]) =>
        response.map((flow) => ({
          id: flow.id,
          name: flow.name,
          description: flow.description,
          createdAt: flow.createdAt,
          updatedAt: flow.updatedAt,
        })),
      providesTags: ['Flows'],
    }),
    getFlowById: builder.query<Flow, { flowId: string }>({
      query: ({ flowId }) => ({
        url: `/${flowId}`,
      }),
      transformResponse: (response: FlowApi) => ({
        id: response.id,
        name: response.name,
        description: response.description,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      providesTags: (_result, _error, { flowId }) => [
        { type: 'Flows' as const, id: flowId },
      ],
    }),
    createFlow: builder.mutation<Flow, NewFlow>({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      transformResponse: (response: FlowApi) => ({
        id: response.id,
        name: response.name,
        description: response.description,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      invalidatesTags: ['Flows'],
    }),
    updateFlow: builder.mutation<Flow, UpdateFlow>({
      query: ({ id, ...body }) => ({
        url: `/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: FlowApi) => ({
        id: response.id,
        name: response.name,
        description: response.description,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Flows',
        { type: 'Flows' as const, id },
      ],
    }),
    deleteFlow: builder.mutation<void, string>({
      query: (flowId) => ({
        url: `/${flowId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Flows'],
    }),
  }),
});

export default flowsService;
export const {
  useGetFlowsQuery,
  useGetFlowByIdQuery,
  useCreateFlowMutation,
  useUpdateFlowMutation,
  useDeleteFlowMutation,
} = flowsService;
